import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Package, Image, Send, Eye, CheckCircle, Clock, Camera, RotateCcw, Download, FileText, Loader2, Mail, XCircle, Pencil, Plus, Trash2, Search, AlertCircle, AlertTriangle, Users, RefreshCw, TrendingUp, ExternalLink, Copy, DollarSign, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { requestWithRetry } from '../../lib/uploadUtils';
import { pollEmailJob } from '../../lib/emailJobs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get proper photo URL (handles both absolute and relative URLs)
const getPhotoUrl = (photo) => {
  const url = photo?.thumbnail_url || photo?.url;
  if (!url) return '';
  // If URL is already absolute (starts with http), use as-is
  if (url.startsWith('http')) return url;
  // Otherwise, prepend the base URL (not API, to avoid double /api)
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Helper to get template URL
const getTemplateUrl = (template) => {
  if (!template) return '';
  const url = template.thumbnail_url || template.url;
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const PreOrdersPage = () => {
  const [preorders, setPreorders] = useState([]);
  const [events, setEvents] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNeedsDetails, setShowNeedsDetails] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const formatMT = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString('en-CA', { timeZone: 'America/Edmonton', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };
  const [showAddonsOnly, setShowAddonsOnly] = useState(false);
  const [assignPhotosDialog, setAssignPhotosDialog] = useState({ open: false, preorder: null });
  const [availablePhotos, setAvailablePhotos] = useState([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const [alreadyAssignedPhotos, setAlreadyAssignedPhotos] = useState([]); // Photos already assigned to this preorder
  const [photoSearchQuery, setPhotoSearchQuery] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [sendingCompleteEmails, setSendingCompleteEmails] = useState(false);
  const [sendingMissingInfoEmails, setSendingMissingInfoEmails] = useState(false);
  const [sendingResendEmails, setSendingResendEmails] = useState(false);
  const [sendingDigitals, setSendingDigitals] = useState(false);
  const [showDigitalsDialog, setShowDigitalsDialog] = useState(false);
  const [pendingDigitals, setPendingDigitals] = useState([]);
  const [loadingDigitals, setLoadingDigitals] = useState(false);
  const [selectedDigitals, setSelectedDigitals] = useState([]);
  const [digitalProgress, setDigitalProgress] = useState({ current: 0, total: 0 });
  const [pendingDigitalCount, setPendingDigitalCount] = useState(0);
  const [recoveringOrders, setRecoveringOrders] = useState(false);
  const [importingOrders, setImportingOrders] = useState(false);
  const [cleaningPhotos, setCleaningPhotos] = useState(false);
  const [assigningPhotos, setAssigningPhotos] = useState(false);
  const [selectedGalleryId, setSelectedGalleryId] = useState(null);
  const [linkedGalleries, setLinkedGalleries] = useState([]);
  
  // View details dialog state
  const [viewDetailsDialog, setViewDetailsDialog] = useState({ open: false, preorder: null, event: null, loading: false });
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, preorder: null, loading: false });
  const [editForm, setEditForm] = useState({
    child_name: '',
    division: '',
    team_name: '',
    jersey_number: '',
    position: '',
    age: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    items: [],
    notes: ''
  });
  const [eventProducts, setEventProducts] = useState([]);

  useEffect(() => {
    fetchPreorders();
    fetchEvents();
    fetchGalleries();
    fetchPendingDigitalCount();
    fetchUpgradeRequests();
    fetchAllProducts();
  }, []);

  const [problemReport, setProblemReport] = useState({ open: false, loading: false, data: null });

  const [allProducts, setAllProducts] = useState([]);
  const [payReq, setPayReq] = useState({ amount: '', description: '', sending: false });

  const fetchAllProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setAllProducts(res.data || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const setEditItemVariation = (index, product, varName, optName) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const vars = { ...(item.selected_variations || {}) };
        if (optName) vars[varName] = optName; else delete vars[varName];
        let price = product.price;
        const labels = [];
        for (const v of product.variations || []) {
          const sel = vars[v.name];
          const opt = (v.options || []).find(o => o.name === sel);
          if (opt) {
            price += opt.price_adjustment || 0;
            labels.push(`${v.name}: ${opt.name}`);
          }
        }
        return {
          ...item,
          selected_variations: vars,
          price: Math.round(price * 100) / 100,
          product_name: product.name + (labels.length ? ` (${labels.join(', ')})` : '')
        };
      })
    }));
  };

  const sendPaymentRequest = async () => {
    const amount = parseFloat(payReq.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setPayReq(prev => ({ ...prev, sending: true }));
    try {
      const res = await axios.post(`${API}/preorders/${editDialog.preorder.id}/request-payment`, {
        amount,
        description: payReq.description || 'Order adjustment',
        base_url: window.location.origin
      });
      toast.success(res.data.email_sent ? 'Payment request sent - client has been emailed' : 'Payment request created (email not sent - no client email on file)');
      setPayReq({ amount: '', description: '', sending: false });
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send payment request');
      setPayReq(prev => ({ ...prev, sending: false }));
    }
  };

  const cancelPaymentRequest = async (preorderId) => {
    try {
      await axios.delete(`${API}/preorders/${preorderId}/request-payment`);
      toast.success('Payment request cancelled');
      fetchPreorders();
      setEditDialog(prev => prev.preorder ? { ...prev, preorder: { ...prev.preorder, admin_payment_request: null } } : prev);
    } catch (error) {
      toast.error('Failed to cancel payment request');
    }
  };

  const [upgradeReqs, setUpgradeReqs] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeActionLoading, setUpgradeActionLoading] = useState(null);

  const fetchUpgradeRequests = async () => {
    try {
      const res = await axios.get(`${API}/preorders/upgrade-requests`);
      setUpgradeReqs(res.data || []);
    } catch (error) {
      console.error('Failed to load upgrade requests:', error);
    }
  };

  const handleUpgradeAction = async (preorderId, action) => {
    if (action === 'reject' && !window.confirm('Reject this upgrade request? The client will keep their original item.')) return;
    setUpgradeActionLoading(preorderId + action);
    try {
      const res = await axios.post(`${API}/preorders/${preorderId}/upgrade-request/${action}`, { base_url: window.location.origin });
      toast.success(res.data.message || `Upgrade ${action}d`);
      fetchUpgradeRequests();
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} upgrade`);
    }
    setUpgradeActionLoading(null);
  };

  const fetchProblemReport = async () => {
    setProblemReport({ open: true, loading: true, data: null });
    try {
      const res = await axios.get(`${API}/preorders/problem-report`);
      setProblemReport({ open: true, loading: false, data: res.data });
    } catch (error) {
      toast.error('Failed to load problem report');
      setProblemReport({ open: false, loading: false, data: null });
    }
  };

  const [bulkApologyLoading, setBulkApologyLoading] = useState(false);
  const [finalsDialog, setFinalsDialog] = useState({ open: false, preorder: null, files: [], uploading: false });

  const openFinalsDialog = async (preorder) => {
    setFinalsDialog({ open: true, preorder, files: [], uploading: false });
    try {
      const res = await axios.get(`${API}/preorders/${preorder.id}/final-files`);
      setFinalsDialog(prev => ({ ...prev, files: res.data.files }));
    } catch {
      toast.error('Failed to load final files');
    }
  };

  const uploadFinalFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setFinalsDialog(prev => ({ ...prev, uploading: true }));
    try {
      const form = new FormData();
      Array.from(fileList).forEach(f => form.append('files', f));
      const res = await axios.post(`${API}/preorders/${finalsDialog.preorder.id}/final-files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000
      });
      setFinalsDialog(prev => ({ ...prev, files: res.data.files, uploading: false }));
      toast.success(`${fileList.length} file(s) uploaded — now on the client's download page`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      setFinalsDialog(prev => ({ ...prev, uploading: false }));
    }
  };

  const deleteFinalFile = async (fileId) => {
    try {
      await axios.delete(`${API}/preorders/${finalsDialog.preorder.id}/final-files/${fileId}`);
      setFinalsDialog(prev => ({ ...prev, files: prev.files.filter(f => f.id !== fileId) }));
      toast.success('File removed');
    } catch {
      toast.error('Failed to remove file');
    }
  };

  const enableDigitalSwap = async () => {
    if (!window.confirm('Enable Digital Swap for all PAID orders still in "Ready for Review" or "Photos Assigned"? Those clients will choose digital products (up to their paid credit) on their review page.')) return;
    try {
      const res = await axios.post(`${API}/preorders/enable-digital-swap`, {});
      toast.success(`${res.data.newly_enabled} order(s) newly enabled (${res.data.total_enabled} total can now choose digital products)`, { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to enable digital swap');
    }
  };

  const grantAllDigitals = async () => {
    const input = window.prompt('Grant ALL photos as digitals to every PAID order with total at or above ($):', '51');
    if (input === null) return;
    const minTotal = parseFloat(input);
    if (isNaN(minTotal) || minTotal <= 0) { toast.error('Enter a valid amount'); return; }
    if (!window.confirm(`Every PAID order of $${minTotal}+ will get all their photos as digital downloads. Continue?`)) return;
    try {
      const res = await axios.post(`${API}/preorders/grant-all-digitals`, { min_total: minTotal });
      toast.success(`${res.data.newly_granted} order(s) newly granted all-digitals (${res.data.total_with_all_digitals} total)`, { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant all-digitals');
    }
  };

  const bulkReopenWithApology = async () => {
    const orders = problemReport.data?.approved_incomplete || [];
    if (orders.length === 0) return;
    if (!window.confirm(`Re-open ${orders.length} order(s) and send each parent an apology email asking them to complete their selections?`)) return;
    setBulkApologyLoading(true);
    try {
      const res = await axios.post(`${API}/preorders/audit/reopen-incomplete`, {
        preorder_ids: orders.map(o => o.id),
        base_url: window.location.origin
      });
      toast.success(`${res.data.reopened} order(s) re-opened, ${res.data.emailed} apology email(s) sent`, { duration: 8000 });
      const failures = (res.data.results || []).filter(r => r.reopened && !r.email_sent);
      if (failures.length > 0) {
        toast.warning(`Email could not be sent for: ${failures.map(f => f.child_name || f.id).join(', ')} (no email on file?)`, { duration: 10000 });
      }
      fetchProblemReport();
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-open orders');
    }
    setBulkApologyLoading(false);
  };

  const reopenPreorder = async (preorder) => {
    if (!window.confirm(`Re-open ${preorder.child_name}'s order? The client will get an email letting them know they can edit their order via their review link. If digital downloads were already released, the order will re-enter your Digital Downloads queue after re-approval.`)) return;
    try {
      const res = await axios.post(`${API}/preorders/${preorder.id}/reopen`, { base_url: window.location.origin });
      if (res.data.email_sent) {
        toast.success('Order re-opened and the client has been emailed their edit link');
      } else {
        toast.warning('Order re-opened, but the notification email could not be sent (no email on file?)');
      }
      setViewDetailsDialog({ open: false, preorder: null, event: null, loading: false });
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-open order');
    }
  };

  const fetchPendingDigitalCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-digital-downloads`);
      setPendingDigitalCount(res.data.pending.length);
    } catch (error) {
      // non-critical badge
    }
  };

  useEffect(() => {
    fetchPreorders();
  }, [selectedEvent, selectedStatus]);

  const fetchPreorders = async () => {
    try {
      let url = `${API}/preorders`;
      const params = new URLSearchParams();
      if (selectedEvent !== 'all') params.append('event_id', selectedEvent);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setPreorders(response.data);
    } catch (error) {
      toast.error('Failed to load pre-orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/preorder-events`);
      setEvents(response.data);
    } catch (error) {
    }
  };

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/galleries`);
      setGalleries(response.data);
    } catch (error) {
    }
  };

  const fetchPhotosForAssignment = async (galleryId) => {
    if (!galleryId) {
      setAvailablePhotos([]);
      return;
    }
    
    try {
      // Special case: fetch from ALL galleries
      if (galleryId === 'all') {
        const allPhotos = [];
        for (const gallery of linkedGalleries) {
          try {
            const response = await axios.get(`${API}/galleries/${gallery.id}/photos`);
            response.data.forEach(photo => {
              photo.gallery_name = gallery.name || 'Unknown Gallery';
              photo.gallery_id = gallery.id;
            });
            allPhotos.push(...response.data);
          } catch (err) {
            console.error(`Failed to load photos from ${gallery.name}:`, err);
          }
        }
        setAvailablePhotos(allPhotos);
        return;
      }
      
      const response = await axios.get(`${API}/galleries/${galleryId}/photos`);
      const gallery = galleries.find(g => g.id === galleryId);
      response.data.forEach(photo => {
        photo.gallery_name = gallery?.name || 'Unknown Gallery';
        photo.gallery_id = galleryId;
      });
      setAvailablePhotos(response.data);
    } catch (error) {
      toast.error('Failed to load photos from gallery');
      setAvailablePhotos([]);
    }
  };

  const openAssignPhotosDialog = async (preorder) => {
    // Find the event's linked galleries
    const event = events.find(e => e.id === preorder.event_id);
    const galleryIds = event?.gallery_ids || [];
    const eventGalleries = galleries.filter(g => galleryIds.includes(g.id));
    
    setLinkedGalleries(eventGalleries);
    setAvailablePhotos([]);
    setAssignPhotosDialog({ open: true, preorder });
    
    // Store the already-assigned photos from the preorder
    const existingPhotos = preorder.assigned_photos || [];
    setAlreadyAssignedPhotos(existingPhotos);
    setSelectedPhotoIds(existingPhotos.map(p => p.id) || []);
    
    if (eventGalleries.length === 0) {
      setSelectedGalleryId(null);
      toast.info('No galleries linked to this event yet. Link galleries first.');
    } else if (eventGalleries.length === 1) {
      // Auto-select if only one gallery
      setSelectedGalleryId(eventGalleries[0].id);
      await fetchPhotosForAssignment(eventGalleries[0].id);
    } else {
      // Multiple galleries - auto-select "All" to show all photos
      setSelectedGalleryId('all');
      // Fetch from all galleries
      const allPhotos = [];
      for (const gallery of eventGalleries) {
        try {
          const response = await axios.get(`${API}/galleries/${gallery.id}/photos`);
          response.data.forEach(photo => {
            photo.gallery_name = gallery.name || 'Unknown Gallery';
            photo.gallery_id = gallery.id;
          });
          allPhotos.push(...response.data);
        } catch (err) {
          console.error(`Failed to load photos from ${gallery.name}:`, err);
        }
      }
      setAvailablePhotos(allPhotos);
    }
  };

  const handleGalleryChange = async (galleryId) => {
    setSelectedGalleryId(galleryId);
    await fetchPhotosForAssignment(galleryId);
  };

  const openViewDetailsDialog = async (preorder) => {
    setViewDetailsDialog({ open: true, preorder: null, event: null, loading: true });
    try {
      const response = await axios.get(`${API}/preorders/${preorder.id}`);
      setViewDetailsDialog({ 
        open: true, 
        preorder: response.data.preorder, 
        event: response.data.event,
        loading: false 
      });
    } catch (error) {
      toast.error('Failed to load pre-order details');
      setViewDetailsDialog({ open: false, preorder: null, event: null, loading: false });
    }
  };

  const handleAssignPhotos = async () => {
    if (!assignPhotosDialog.preorder) return;
    setAssigningPhotos(true);
    try {
      await requestWithRetry(() => axios.put(
        `${API}/preorders/${assignPhotosDialog.preorder.id}/assign-photos`,
        selectedPhotoIds
      ));
      toast.success('Photos assigned');
      setAssignPhotosDialog({ open: false, preorder: null });
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign photos - please try again');
    } finally {
      setAssigningPhotos(false);
    }
  };

  const handleMarkReady = async (preorderId) => {
    try {
      const baseUrl = window.location.origin;
      const response = await axios.put(`${API}/preorders/${preorderId}/ready-for-review?base_url=${encodeURIComponent(baseUrl)}`);
      
      if (response.data.email_sent) {
        toast.success('Photos ready! Email sent to client.');
      } else {
        toast.success('Pre-order marked ready for review');
        // Show the review URL if email wasn't sent
        if (response.data.review_url) {
          const fullUrl = `${baseUrl}${response.data.review_url}`;
          toast.info(`Review link (email not sent): ${fullUrl}`, { duration: 10000 });
        }
      }
      
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark ready');
    }
  };

  // Payment dialog state
  const [paymentDialog, setPaymentDialog] = useState({ open: false, preorder: null });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');

  const handleMarkPaid = async () => {
    if (!paymentDialog.preorder) return;
    
    try {
      const preorder = paymentDialog.preorder;
      const total = preorder.total || 0;
      const amountPaid = preorder.amount_paid || 0;
      const balance = total - amountPaid;
      
      // Parse payment amount (use balance if empty)
      const amount = paymentAmount ? parseFloat(paymentAmount) : balance;
      
      await axios.put(`${API}/preorders/${preorder.id}/mark-paid`, {
        payment_method: paymentMethod,
        notes: paymentNotes,
        amount: amount
      });
      
      const isPartial = amount < balance;
      toast.success(isPartial 
        ? `Partial payment of $${amount.toFixed(2)} recorded. Balance: $${(balance - amount).toFixed(2)}`
        : `Pre-order marked as fully paid`
      );
      setPaymentDialog({ open: false, preorder: null });
      setPaymentAmount('');
      setPaymentNotes('');
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const openPaymentDialog = (preorder) => {
    setPaymentDialog({ open: true, preorder });
    setPaymentAmount('');
    setPaymentMethod('cash');
    setPaymentNotes('');
  };

  const handleUpdateStatus = async (preorderId, status) => {
    try {
      await axios.put(`${API}/preorders/${preorderId}/status?status=${status}`);
      toast.success(`Status updated to ${status}`);
      fetchPreorders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Open edit dialog
  const openEditDialog = async (preorder) => {
    setEditDialog({ open: true, preorder, loading: true });
    
    // Fetch event to get available products
    try {
      const eventRes = await axios.get(`${API}/preorder-events/${preorder.event_id}`);
      setEventProducts(eventRes.data.products || []);
    } catch (error) {
      console.error('Failed to fetch event products:', error);
      setEventProducts([]);
    }
    
    // Populate form with current values
    setEditForm({
      child_name: preorder.child_name || '',
      division: preorder.division || '',
      team_name: preorder.team_name || '',
      jersey_number: preorder.jersey_number || '',
      position: preorder.position || '',
      age: preorder.age || '',
      client_name: preorder.client_name || preorder.parent_name || '',
      client_email: preorder.client_email || preorder.parent_email || '',
      client_phone: preorder.client_phone || preorder.parent_phone || '',
      items: preorder.items || [],
      notes: preorder.admin_notes || ''
    });
    
    setEditDialog({ open: true, preorder, loading: false });
  };

  // Save edited preorder
  const savePreorderEdit = async () => {
    if (!editDialog.preorder) return;
    
    try {
      setEditDialog(prev => ({ ...prev, loading: true }));
      
      await axios.put(`${API}/preorders/${editDialog.preorder.id}/edit`, {
        child_name: editForm.child_name,
        division: editForm.division,
        team_name: editForm.team_name,
        jersey_number: editForm.jersey_number,
        position: editForm.position,
        age: editForm.age,
        client_name: editForm.client_name,
        client_email: editForm.client_email,
        client_phone: editForm.client_phone,
        items: editForm.items,
        notes: editForm.notes
      });
      
      toast.success('Pre-order updated successfully');
      setEditDialog({ open: false, preorder: null, loading: false });
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pre-order');
      setEditDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Add item to edit form
  const addItemToEdit = (product) => {
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      price: product.price,
      quantity: 1,
      selected_variations: {},
      customizations: {}
    };
    setEditForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // Remove item from edit form
  const removeItemFromEdit = (index) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Update item quantity
  const updateItemQuantity = (index, quantity) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    }));
  };

  // Calculate edit form totals
  const getEditSubtotal = () => {
    return editForm.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotoIds(prev => 
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleResendFreeDigital = async (preorderId) => {
    try {
      await axios.post(`${API}/preorders/${preorderId}/resend-free-digital`);
      toast.success('Free digital image resent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend free digital');
    }
  };

  const [resendDigital, setResendDigital] = useState({ open: false, preorder: null, photoId: null, backgroundId: '', sending: false });
  const [backgroundsList, setBackgroundsList] = useState([]);

  const openResendDigitalDialog = async (preorder) => {
    setResendDigital({
      open: true,
      preorder,
      photoId: preorder.free_digital_photo_id || null,
      backgroundId: preorder.free_digital_background_id || '',
      sending: false
    });
    if (backgroundsList.length === 0) {
      try {
        const res = await axios.get(`${API}/backgrounds`);
        setBackgroundsList((res.data || []).filter(b => b.active !== false));
      } catch (error) {
        console.error('Failed to load backgrounds:', error);
      }
    }
  };

  const sendResendDigital = async () => {
    if (!resendDigital.photoId) {
      toast.error('Choose a photo first');
      return;
    }
    setResendDigital(prev => ({ ...prev, sending: true }));
    try {
      await axios.post(`${API}/preorders/${resendDigital.preorder.id}/resend-free-digital`, {
        photo_id: resendDigital.photoId,
        background_id: resendDigital.backgroundId || null
      });
      toast.success('Free digital image resent with the selected background!');
      setResendDigital({ open: false, preorder: null, photoId: null, backgroundId: '', sending: false });
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend free digital');
      setResendDigital(prev => ({ ...prev, sending: false }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  // Order currently contains items purchased after the original pre-order (upsell add-ons)
  const hasAddons = (p) => p.items?.some(i => i.is_addon);
  const hasUpgrades = (p) => p.items?.some(i => i.upgraded_from);

  // Filter preorders by search query, needs details toggle, payment type, and date
  const filteredPreorders = preorders.filter(preorder => {
    // First check "Needs Details" filter
    if (showNeedsDetails) {
      if (!(preorder.recovered_from_stripe && preorder.details_completed !== true)) {
        return false;
      }
    }
    
    // "Has Add-Ons" filter
    if (showAddonsOnly && !hasAddons(preorder)) return false;
    
    // Payment method filter
    if (paymentFilter !== 'all') {
      const paymentStatus = preorder.payment_status || '';
      const paymentMethod = preorder.payment_method || '';
      
      if (paymentFilter === 'paid' && paymentStatus !== 'paid') return false;
      if (paymentFilter === 'pending_cash' && paymentStatus !== 'pending_cash') return false;
      if (paymentFilter === 'pending_etransfer' && paymentStatus !== 'pending_etransfer') return false;
      if (paymentFilter === 'stripe' && paymentMethod !== 'stripe') return false;
      if (paymentFilter === 'unpaid' && !['unpaid', 'pending'].includes(paymentStatus)) return false;
    }
    
    // Date filter
    if (dateFilter !== 'all') {
      const orderDate = preorder.created_at ? new Date(preorder.created_at) : null;
      if (!orderDate) return false;
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      if (dateFilter === 'today' && orderDate < today) return false;
      if (dateFilter === 'week' && orderDate < weekAgo) return false;
      if (dateFilter === 'month' && orderDate < monthAgo) return false;
    }
    
    // Then apply search query
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      // Child names
      preorder.child_name?.toLowerCase().includes(query) ||
      preorder.child_first_name?.toLowerCase().includes(query) ||
      preorder.child_last_name?.toLowerCase().includes(query) ||
      // Parent/Client names
      preorder.client_name?.toLowerCase().includes(query) ||
      preorder.parent_name?.toLowerCase().includes(query) ||
      preorder.parent_first_name?.toLowerCase().includes(query) ||
      preorder.parent_last_name?.toLowerCase().includes(query) ||
      // Contact info
      preorder.client_email?.toLowerCase().includes(query) ||
      preorder.parent_email?.toLowerCase().includes(query) ||
      preorder.client_phone?.toLowerCase().includes(query) ||
      preorder.parent_phone?.toLowerCase().includes(query) ||
      // Team info
      preorder.team_name?.toLowerCase().includes(query) ||
      preorder.division?.toLowerCase().includes(query) ||
      preorder.jersey_number?.toLowerCase().includes(query) ||
      // Order ID
      preorder.id?.toLowerCase().includes(query) ||
      preorder.id?.toLowerCase().startsWith(query) ||
      // Review link code (e.g. 8A87D93C from the parent's link)
      preorder.review_token?.toLowerCase().includes(query)
    );
  });

  const displayedPreorders = sortBy === 'recently_approved'
    ? [...filteredPreorders].sort((a, b) => (b.approved_at || '').localeCompare(a.approved_at || ''))
    : filteredPreorders;

  const getStatusBadge = (status, paymentStatus) => {
    if (status === 'cancelled') {
      return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>;
    }
    if (paymentStatus === 'pending_etransfer') {
      return <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50">Pending e-Transfer</Badge>;
    }
    if (paymentStatus === 'pending_cash') {
      return <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">Pending Cash</Badge>;
    }
    if (paymentStatus !== 'paid') {
      return <Badge variant="outline" className="text-red-600 border-red-300">Unpaid</Badge>;
    }
    
    const statusConfig = {
      awaiting_photos: { label: 'Awaiting Photos', className: 'bg-amber-100 text-amber-700' },
      photos_assigned: { label: 'Photos Assigned', className: 'bg-blue-100 text-blue-700' },
      ready_for_review: { label: 'Ready for Review', className: 'bg-purple-100 text-purple-700' },
      approved: { label: 'Approved', className: 'bg-green-100 text-green-700' },
      fulfilled: { label: 'Fulfilled', className: 'bg-slate-100 text-slate-700' }
    };
    
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getEventName = (eventId) => {
    return events.find(e => e.id === eventId)?.name || 'Unknown Event';
  };

  const exportToExcel = async () => {
    setExportingExcel(true);
    try {
      const params = new URLSearchParams();
      if (selectedEvent !== 'all') params.append('event_id', selectedEvent);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      
      const response = await axios.get(`${API}/preorders/export/excel?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preorders_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Pre-orders exported to Excel');
    } catch (error) {
      toast.error('Failed to export pre-orders');
    }
    setExportingExcel(false);
  };

  const exportForGalleries = async () => {
    setExportingExcel(true);
    try {
      const params = new URLSearchParams();
      if (selectedEvent !== 'all') params.append('event_id', selectedEvent);
      if (paymentFilter !== 'all') params.append('payment_status', paymentFilter);
      
      const response = await axios.get(`${API}/preorders/export-for-galleries?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_for_galleries_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Client list exported as CSV for bulk galleries');
    } catch (error) {
      toast.error('Failed to export client list');
    }
    setExportingExcel(false);
  };

  const sendCompleteOrderEmails = async () => {
    // Count recovered orders first
    const recoveredOrders = preorders.filter(p => p.recovered_from_stripe && !p.details_completed);
    if (recoveredOrders.length === 0) {
      toast.info('No recovered orders need email notifications');
      return;
    }
    
    if (!window.confirm(`Send "Complete Your Order" emails to ${recoveredOrders.length} customers?\n\nThis will email parents asking them to fill in their child's name and other details.`)) {
      return;
    }
    
    setSendingCompleteEmails(true);
    try {
      const response = await axios.post(`${API}/email/send-complete-order-requests`);
      toast.success(`Sent ${response.data.sent} emails successfully!`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} emails failed to send`);
      }
      fetchPreorders(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    }
    setSendingCompleteEmails(false);
  };

  const recoverStripeOrders = async () => {
    if (!window.confirm('Scan Stripe for missing paid orders?\n\nThis will find any orders that were paid but not saved to your database and recover them.')) {
      return;
    }
    
    setRecoveringOrders(true);
    try {
      const response = await axios.post(`${API}/admin/recover-stripe-orders`);
      if (response.data.recovered > 0) {
        toast.success(response.data.message);
        fetchPreorders(); // Refresh to show recovered orders
      } else {
        toast.info('No missing orders found - all Stripe payments are already in your database!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan Stripe orders');
    }
    setRecoveringOrders(false);
  };

  const importPreviewOrders = async () => {
    if (!window.confirm('Import all orders from the preview/test environment?\n\nThis will add 162 orders (cash, e-transfer, and card payments) that were placed on the preview site.\n\nOrders that already exist will be skipped.')) {
      return;
    }
    
    setImportingOrders(true);
    try {
      const response = await axios.post(`${API}/admin/import-preview-orders`);
      toast.success(response.data.message);
      if (response.data.skipped > 0) {
        toast.info(`${response.data.skipped} orders were skipped (already exist)`);
      }
      fetchPreorders(); // Refresh to show imported orders
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import preview orders');
    }
    setImportingOrders(false);
  };

  const forceImportPreviewOrders = async () => {
    if (!window.confirm('⚠️ FORCE IMPORT all 162 preview orders?\n\nThis will OVERWRITE any existing orders with the same ID.\n\nUse this if the normal import says orders exist but you cannot see them.')) {
      return;
    }
    
    setImportingOrders(true);
    try {
      const response = await axios.post(`${API}/admin/force-import-preview-orders`);
      toast.success(response.data.message);
      fetchPreorders(); // Refresh to show imported orders
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force import orders');
    }
    setImportingOrders(false);
  };

  const cleanupBrokenPhotos = async () => {
    if (!window.confirm('🧹 Cleanup broken photos?\n\nThis will:\n• Delete photo records with missing files\n• Sync gallery photo counts\n• Fix "X photos" showing but 0 displayed\n\nThis cannot be undone.')) {
      return;
    }
    
    setCleaningPhotos(true);
    try {
      const response = await axios.post(`${API}/admin/cleanup-broken-photos`);
      toast.success(response.data.message);
      if (response.data.galleries_affected?.length > 0) {
        toast.info(`Fixed ${response.data.galleries_affected.length} galleries`);
      }
      fetchGalleries(); // Refresh galleries list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup broken photos');
    }
    setCleaningPhotos(false);
  };

  // Count orders missing info (no child name or empty child_first_name/child_last_name)
  const ordersMissingInfo = preorders.filter(p => {
    const hasChildName = p.child_name && p.child_name !== 'N/A' && p.child_name !== 'Unknown' && p.child_name.trim() !== '';
    const hasChildFirstLast = p.child_first_name && p.child_first_name.trim() !== '' && p.child_last_name && p.child_last_name.trim() !== '';
    const detailsCompleted = p.details_completed === true;
    const alreadyEmailed = p.missing_info_email_sent === true;
    return !hasChildName && !hasChildFirstLast && !detailsCompleted && !alreadyEmailed;
  });

  const sendMissingInfoEmails = async () => {
    if (!window.confirm(`Send "Complete Your Order" emails to ${ordersMissingInfo.length} orders missing child information?\n\nThis will send emails with links to complete their order details.`)) {
      return;
    }
    
    setSendingMissingInfoEmails(true);
    try {
      const response = await axios.post(`${API}/email/send-missing-info-emails`);
      toast.success(response.data.message);
      if (response.data.skipped > 0) {
        toast.info(`${response.data.skipped} orders were skipped (no email or already emailed)`);
      }
      fetchPreorders(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    }
    setSendingMissingInfoEmails(false);
  };

  const openDigitalsDialog = async () => {
    setShowDigitalsDialog(true);
    setLoadingDigitals(true);
    setSelectedDigitals([]);
    try {
      const res = await axios.get(`${API}/admin/pending-digital-downloads`);
      setPendingDigitals(res.data.pending);
      setPendingDigitalCount(res.data.pending.length);
    } catch (error) {
      toast.error('Failed to load pending digital downloads');
    }
    setLoadingDigitals(false);
  };

  const toggleDigital = (pid) => {
    setSelectedDigitals(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };

  const selectAllDigitals = () => {
    const ready = pendingDigitals.filter(p => p.complete).map(p => p.preorder_id);
    setSelectedDigitals(prev => prev.length === ready.length ? [] : ready);
  };

  const releaseDigitals = async () => {
    setSendingDigitals(true);
    setDigitalProgress({ current: 0, total: selectedDigitals.length });
    try {
      const res = await axios.post(`${API}/admin/send-digital-downloads`, {
        preorder_ids: selectedDigitals,
        base_url: window.location.origin
      }, { timeout: 60000 });
      let agg = res.data;
      if (res.data.status === 'queued') {
        agg = await pollEmailJob(res.data.job_id, (j) => {
          setDigitalProgress({ current: j.processed, total: j.total });
        });
      }
      toast.success(`Released ${agg.sent.length} digital download email${agg.sent.length === 1 ? '' : 's'}!`);
      if ((agg.failed || []).length > 0) toast.error(`${agg.failed.length} failed to send`);
      if ((agg.skipped || []).length > 0) {
        const reasons = [...new Set(agg.skipped.map(s => s.reason))].join(', ');
        toast.warning(`${agg.skipped.length} skipped (${reasons})`);
      }
      setSelectedDigitals([]);
      const refreshed = await axios.get(`${API}/admin/pending-digital-downloads`);
      setPendingDigitals(refreshed.data.pending);
      setPendingDigitalCount(refreshed.data.pending.length);
      fetchPreorders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digital downloads');
    }
    setSendingDigitals(false);
    setDigitalProgress({ current: 0, total: 0 });
  };

  // Count orders that NEED DETAILS (recovered_from_stripe && !details_completed)
  const ordersNeedingDetails = preorders.filter(p => p.recovered_from_stripe && p.details_completed !== true);

  const resendAllEmails = async () => {
    if (!window.confirm(`Resend "Verify Order Details" emails to ${ordersNeedingDetails.length} orders that need details?\n\nThis will send emails to all orders showing "Needs Details" badge, even if they were previously emailed.\n\nUse this if previous emails had wrong links.`)) {
      return;
    }
    
    setSendingResendEmails(true);
    try {
      const response = await axios.post(`${API}/email/resend-complete-order-emails`);
      toast.success(response.data.message);
      if (response.data.skipped > 0) {
        toast.info(`${response.data.skipped} orders were skipped (no email address or token)`);
      }
      fetchPreorders(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    }
    setSendingResendEmails(false);
  };

  return (
    <div className="space-y-6" data-testid="preorders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-stone-800">Pre-Orders</h1>
          <p className="text-stone-600">Manage pre-orders and assign photos</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap items-center justify-between">
        <div className="flex gap-4 flex-wrap items-center">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
              data-testid="search-preorders"
            />
          </div>
          
          <Select value={selectedEvent} onValueChange={setSelectedEvent}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="awaiting_photos">Awaiting Photos</SelectItem>
              <SelectItem value="photos_assigned">Photos Assigned</SelectItem>
              <SelectItem value="ready_for_review">Ready for Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="fulfilled">Fulfilled</SelectItem>
            </SelectContent>
          </Select>

          <Select value={paymentFilter} onValueChange={setPaymentFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Payment type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="stripe">Stripe/Card</SelectItem>
              <SelectItem value="pending_cash">Pending Cash</SelectItem>
              <SelectItem value="pending_etransfer">Pending e-Transfer</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
            </SelectContent>
          </Select>

          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-52" data-testid="sort-by-select">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest Orders First</SelectItem>
              <SelectItem value="recently_approved">Recently Approved First</SelectItem>
            </SelectContent>
          </Select>

          {/* Problem Report */}
          <Button
            variant="outline"
            onClick={fetchProblemReport}
            className="gap-2 border-red-400 text-red-600 hover:bg-red-50"
            data-testid="problem-report-btn"
          >
            <AlertTriangle className="h-4 w-4" />
            Problem Report
          </Button>

          <Button
            variant="outline"
            className="gap-2 text-purple-700 border-purple-300 hover:bg-purple-50"
            onClick={enableDigitalSwap}
            data-testid="enable-digital-swap-btn"
          >
            <Download className="h-4 w-4" />
            Enable Digital Swap
          </Button>

          {/* Has Add-Ons Toggle */}
          <Button
            variant={showAddonsOnly ? "default" : "outline"}
            onClick={() => setShowAddonsOnly(!showAddonsOnly)}
            className={showAddonsOnly
              ? "gap-2 bg-purple-600 hover:bg-purple-700 text-white"
              : "gap-2 border-purple-400 text-purple-600 hover:bg-purple-50"}
            data-testid="toggle-addons-filter-btn"
          >
            <Plus className="h-4 w-4" />
            Has Add-Ons ({preorders.filter(hasAddons).length})
          </Button>

          {/* Upgrade Requests */}
          <Button
            variant="outline"
            onClick={() => setUpgradeModalOpen(true)}
            className="gap-2 border-blue-400 text-blue-600 hover:bg-blue-50"
            data-testid="upgrade-requests-btn"
          >
            <TrendingUp className="h-4 w-4" />
            Upgrade Requests ({upgradeReqs.filter(r => r.upgrade_request?.status === 'pending_admin').length})
          </Button>

          {/* Needs Details Toggle */}
          {ordersNeedingDetails.length > 0 && (
            <Button
              variant={showNeedsDetails ? "default" : "outline"}
              onClick={() => setShowNeedsDetails(!showNeedsDetails)}
              className={showNeedsDetails 
                ? "gap-2 bg-amber-500 hover:bg-amber-600 text-white" 
                : "gap-2 border-amber-400 text-amber-600 hover:bg-amber-50"}
              data-testid="toggle-needs-details-btn"
            >
              <AlertCircle className="h-4 w-4" />
              Needs Details ({ordersNeedingDetails.length})
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-500">
            {filteredPreorders.length} of {preorders.length} orders
          </span>
          {/* Import Preview Orders button */}
          <Button
            variant="outline"
            onClick={importPreviewOrders}
            disabled={importingOrders}
            data-testid="import-preview-orders-btn"
            className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            <Download className="h-4 w-4" />
            {importingOrders ? 'Importing...' : 'Import Preview Orders'}
          </Button>
          {/* Force Import button - for when normal import doesn't work */}
          <Button
            variant="outline"
            onClick={forceImportPreviewOrders}
            disabled={importingOrders}
            data-testid="force-import-preview-orders-btn"
            className="gap-2 border-red-500 text-red-600 hover:bg-red-50"
          >
            <Download className="h-4 w-4" />
            {importingOrders ? 'Importing...' : 'Force Import (162)'}
          </Button>
          {/* Cleanup broken photos button */}
          <Button
            variant="outline"
            onClick={cleanupBrokenPhotos}
            disabled={cleaningPhotos}
            data-testid="cleanup-photos-btn"
            className="gap-2 border-orange-500 text-orange-600 hover:bg-orange-50"
            title="Fix galleries showing photos that don't exist"
          >
            <Trash2 className="h-4 w-4" />
            {cleaningPhotos ? 'Cleaning...' : 'Cleanup Broken Photos'}
          </Button>
          {/* Recover Orders from Stripe button */}
          <Button
            variant="outline"
            onClick={recoverStripeOrders}
            disabled={recoveringOrders}
            data-testid="recover-stripe-orders-btn"
            className="gap-2 border-green-500 text-green-600 hover:bg-green-50"
          >
            <RotateCcw className="h-4 w-4" />
            {recoveringOrders ? 'Scanning Stripe...' : 'Recover Missing Orders'}
          </Button>
          {/* Show button if there are recovered orders needing details AND not yet emailed */}
          {preorders.filter(p => p.recovered_from_stripe && !p.details_completed && !p.complete_order_email_sent).length > 0 && (
            <Button
              variant="outline"
              onClick={sendCompleteOrderEmails}
              disabled={sendingCompleteEmails}
              data-testid="send-complete-emails-btn"
              className="gap-2 border-amber-500 text-amber-600 hover:bg-amber-50"
            >
              <Mail className="h-4 w-4" />
              {sendingCompleteEmails ? 'Sending...' : `Email ${preorders.filter(p => p.recovered_from_stripe && !p.details_completed && !p.complete_order_email_sent).length} Parents`}
            </Button>
          )}
          {/* Button to email ALL orders missing child info */}
          {ordersMissingInfo.length > 0 && (
            <Button
              variant="outline"
              onClick={sendMissingInfoEmails}
              disabled={sendingMissingInfoEmails}
              data-testid="send-missing-info-emails-btn"
              className="gap-2 border-purple-500 text-purple-600 hover:bg-purple-50"
            >
              <Mail className="h-4 w-4" />
              {sendingMissingInfoEmails ? 'Sending...' : `Email ${ordersMissingInfo.length} Missing Info`}
            </Button>
          )}
          {/* Button to RESEND emails to orders needing details */}
          {ordersNeedingDetails.length > 0 && (
            <Button
              variant="outline"
              onClick={resendAllEmails}
              disabled={sendingResendEmails}
              data-testid="resend-all-emails-btn"
              className="gap-2 border-green-500 text-green-600 hover:bg-green-50"
            >
              <Mail className="h-4 w-4" />
              {sendingResendEmails ? 'Sending...' : `Resend to ${ordersNeedingDetails.length} Needs Details`}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={openDigitalsDialog}
            data-testid="send-digital-downloads-btn"
            className="gap-2 border-blue-500 text-blue-600 hover:bg-blue-50 relative"
          >
            <Download className="h-4 w-4" />
            Digital Downloads
            {pendingDigitalCount > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center"
                data-testid="pending-digitals-badge"
              >
                {pendingDigitalCount}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={exportToExcel}
            disabled={exportingExcel || preorders.length === 0}
            data-testid="export-excel-btn"
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {exportingExcel ? 'Exporting...' : 'Export to Excel'}
          </Button>
          <Button
            variant="outline"
            onClick={exportForGalleries}
            disabled={exportingExcel || preorders.length === 0}
            data-testid="export-galleries-btn"
            className="gap-2 border-purple-500 text-purple-600 hover:bg-purple-50"
          >
            <Users className="h-4 w-4" />
            Download for Bulk Galleries
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">
              {preorders.filter(p => p.payment_status === 'pending_etransfer').length}
            </p>
            <p className="text-sm text-stone-500">Pending e-Transfer</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-stone-800">
              {preorders.filter(p => p.payment_status === 'paid').length}
            </p>
            <p className="text-sm text-stone-500">Paid Orders</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">
              {preorders.filter(p => p.status === 'awaiting_photos' && p.payment_status === 'paid').length}
            </p>
            <p className="text-sm text-stone-500">Awaiting Photos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {preorders.filter(p => p.status === 'photos_assigned').length}
            </p>
            <p className="text-sm text-stone-500">Photos Assigned</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">
              {preorders.filter(p => p.status === 'ready_for_review').length}
            </p>
            <p className="text-sm text-stone-500">Pending Review</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {preorders.filter(p => p.status === 'approved' || p.status === 'fulfilled').length}
            </p>
            <p className="text-sm text-stone-500">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Pre-orders Table */}
      {loading ? (
        <div className="h-48 bg-slate-200 rounded-xl animate-pulse" />
      ) : filteredPreorders.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Child/Client</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Photos</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedPreorders.map((preorder, poIdx) => (
                  <TableRow key={`${preorder.id}-${poIdx}`}>
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{preorder.child_name}</p>
                          {preorder.recovered_from_stripe && !preorder.details_completed && (
                            <Badge 
                              variant="outline" 
                              className="text-xs bg-amber-50 text-amber-700 border-amber-300 cursor-pointer hover:bg-amber-100"
                              onClick={async () => {
                                if (window.confirm('Mark this order\'s details as complete?')) {
                                  try {
                                    await axios.put(`${API}/preorders/${preorder.id}/mark-details-complete`);
                                    toast.success('Order details marked as complete');
                                    fetchPreorders();
                                  } catch (error) {
                                    toast.error('Failed to update order');
                                  }
                                }
                              }}
                              title="Click to mark details as complete"
                            >
                              Needs Details
                            </Badge>
                          )}
                          {preorder.details_completed && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Complete
                            </Badge>
                          )}
                          {hasAddons(preorder) && (
                            <Badge
                              variant="outline"
                              className="text-xs bg-purple-50 text-purple-700 border-purple-300"
                              title={preorder.addon_paid_at ? `Add-on paid ${new Date(preorder.addon_paid_at).toLocaleString()}` : 'Client added items after the original pre-order'}
                              data-testid={`addon-badge-${preorder.id}`}
                            >
                              <Plus className="h-3 w-3 mr-0.5" />
                              Add-On
                            </Badge>
                          )}
                          {hasUpgrades(preorder) && (
                            <Badge
                              variant="outline"
                              className="text-xs bg-blue-50 text-blue-700 border-blue-300"
                              title={`Upgraded: ${preorder.items.filter(i => i.upgraded_from).map(i => `${i.upgraded_from.name} → ${i.package_name || i.product_name}`).join('; ')}`}
                              data-testid={`upgraded-badge-${preorder.id}`}
                            >
                              <TrendingUp className="h-3 w-3 mr-0.5" />
                              Upgraded
                            </Badge>
                          )}
                          {preorder.admin_payment_request?.status === 'pending' && (
                            <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-300" title={preorder.admin_payment_request.description} data-testid={`payment-requested-badge-${preorder.id}`}>
                              <DollarSign className="h-3 w-3 mr-0.5" />
                              Payment Requested ({formatCurrency(preorder.admin_payment_request.amount)})
                            </Badge>
                          )}
                          {preorder.admin_payment_request?.status === 'paid' && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300" title={preorder.admin_payment_request.description} data-testid={`payment-received-badge-${preorder.id}`}>
                              <DollarSign className="h-3 w-3 mr-0.5" />
                              Extra Payment Received
                            </Badge>
                          )}
                          {preorder.upgrade_request && (
                            <Badge
                              variant="outline"
                              className="text-xs bg-amber-50 text-amber-700 border-amber-300 cursor-pointer hover:bg-amber-100"
                              onClick={() => setUpgradeModalOpen(true)}
                              title="Click to review upgrade requests"
                              data-testid={`upgrade-pending-badge-${preorder.id}`}
                            >
                              <TrendingUp className="h-3 w-3 mr-0.5" />
                              {preorder.upgrade_request.status === 'pending_admin' ? 'Upgrade Requested' : 'Upgrade Pending Payment'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{preorder.client_name}</p>
                        {preorder.team_name && (
                          <p className="text-xs text-slate-400">{preorder.team_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{getEventName(preorder.event_id)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {preorder.created_at ? (
                          <>
                            <div>{new Date(preorder.created_at).toLocaleDateString()}</div>
                            <div className="text-xs text-slate-400">
                              {new Date(preorder.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                          </>
                        ) : (
                          <span className="text-slate-400">N/A</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {preorder.items.map((item, idx) => (
                          <div key={`${item.product_id || item.package_id || 'item'}-${idx}`} className={item.is_addon ? 'text-purple-700' : ''}>
                            {item.package_name || item.product_name} x{item.quantity}
                            {item.is_addon && <span className="text-purple-500 text-xs ml-1">(added later)</span>}
                            {item.upgraded_from && <span className="text-blue-500 text-xs ml-1">(upgraded from {item.upgraded_from.name})</span>}
                          </div>
                        ))}
                        {preorder.includes_free_digital && (
                          <div className="text-green-600 text-xs">+ Free Digital</div>
                        )}
                        {preorder.includes_free_wallets && (
                          <div className="text-blue-600 text-xs">+ Free Wallets</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(preorder.total)}</TableCell>
                    <TableCell>
                      {getStatusBadge(preorder.status, preorder.payment_status)}
                      {preorder.approved_at && (
                        <div className="text-xs text-green-700 mt-1 whitespace-nowrap" data-testid={`approved-at-${preorder.id}`}>
                          ✓ Approved {formatMT(preorder.approved_at)}
                        </div>
                      )}
                      {preorder.reopened_at && (!preorder.approved_at || new Date(preorder.reopened_at) > new Date(preorder.approved_at)) && (
                        <div className="text-xs text-amber-600 mt-0.5 whitespace-nowrap" data-testid={`reopened-at-${preorder.id}`}>
                          ↺ Re-opened {formatMT(preorder.reopened_at)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Camera className="h-4 w-4 text-slate-400" />
                        <span>{preorder.assigned_photos?.length || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {/* View Details button - always visible */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openViewDetailsDialog(preorder)}
                          title="View Order Details"
                          data-testid={`view-details-${preorder.id}`}
                        >
                          <FileText className="h-3 w-3" />
                        </Button>
                        
                        {/* Upload final designed files for digital delivery */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openFinalsDialog(preorder)}
                          title="Final Files (upload finished designs for digital delivery)"
                          className="text-purple-600 border-purple-300 hover:bg-purple-50"
                          data-testid={`final-files-${preorder.id}`}
                        >
                          <Upload className="h-3 w-3" />
                        </Button>
                        
                        {/* Re-open approved order for client edits */}
                        {['approved', 'processing', 'fulfilled', 'completed'].includes(preorder.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => reopenPreorder(preorder)}
                            title="Re-open Order for Client (emails them their edit link)"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            data-testid={`reopen-row-${preorder.id}`}
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        )}
                        
                        {/* View as client / copy review link */}
                        {preorder.review_token && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`${window.location.origin}/preorder/review/${preorder.review_token}`, '_blank')}
                              title="View as Client (opens their review page)"
                              className="text-teal-600 border-teal-300 hover:bg-teal-50"
                              data-testid={`view-as-client-${preorder.id}`}
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const link = `${window.location.origin}/preorder/review/${preorder.review_token}`;
                                try {
                                  navigator.clipboard.writeText(link).catch(() => {
                                    const ta = document.createElement('textarea');
                                    ta.value = link;
                                    document.body.appendChild(ta);
                                    ta.select();
                                    document.execCommand('copy');
                                    document.body.removeChild(ta);
                                  });
                                } catch (e) {
                                  const ta = document.createElement('textarea');
                                  ta.value = link;
                                  document.body.appendChild(ta);
                                  ta.select();
                                  document.execCommand('copy');
                                  document.body.removeChild(ta);
                                }
                                toast.success('Client review link copied to clipboard');
                              }}
                              title="Copy Client Review Link"
                              data-testid={`copy-review-link-${preorder.id}`}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        
                        {/* Edit button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(preorder)}
                          title="Edit Order"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          data-testid={`edit-order-${preorder.id}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        
                        {/* Cancel button - visible for non-cancelled orders */}
                        {preorder.status !== 'cancelled' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to cancel the order for ${preorder.child_name}?`)) {
                                handleUpdateStatus(preorder.id, 'cancelled');
                              }
                            }}
                            title="Cancel Order"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            data-testid={`cancel-order-${preorder.id}`}
                          >
                            <XCircle className="h-3 w-3" />
                          </Button>
                        )}
                        
                        {/* Restore button - visible for cancelled orders */}
                        {preorder.status === 'cancelled' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const restoreTo = preorder.previous_status ||
                                ((preorder.assigned_photos || []).length > 0 ? 'photos_assigned' : 'awaiting_photos');
                              if (window.confirm(`Restore ${preorder.child_name}'s order? It will go back to "${restoreTo.replace(/_/g, ' ')}".`)) {
                                handleUpdateStatus(preorder.id, restoreTo);
                              }
                            }}
                            title="Restore Cancelled Order"
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            data-testid={`restore-order-${preorder.id}`}
                          >
                            <RotateCcw className="h-3 w-3" />
                          </Button>
                        )}
                        
                        {/* Mark as Paid button - different style for e-transfer */}
                        {preorder.payment_status === 'pending_etransfer' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPaymentDialog(preorder)}
                            title="Mark e-Transfer as Received"
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            data-testid={`mark-paid-${preorder.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            e-Transfer Received
                          </Button>
                        )}
                        {preorder.payment_status === 'unpaid' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPaymentDialog(preorder)}
                            title="Record Payment"
                            className="text-green-600"
                            data-testid={`mark-paid-${preorder.id}`}
                          >
                            $
                          </Button>
                        )}
                        {preorder.payment_status === 'pending_cash' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPaymentDialog(preorder)}
                            title="Record Cash Payment"
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            data-testid={`mark-paid-cash-${preorder.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Cash Received
                          </Button>
                        )}
                        {preorder.payment_status === 'partial' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPaymentDialog(preorder)}
                            title="Record Additional Payment"
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            data-testid={`add-payment-${preorder.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Add Payment
                          </Button>
                        )}
                        {preorder.payment_status === 'paid' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openAssignPhotosDialog(preorder)}
                              title="Assign Photos"
                              data-testid={`assign-photos-${preorder.id}`}
                            >
                              <Image className="h-3 w-3" />
                            </Button>
                            
                            {/* Toggleable Status Buttons - Always visible for paid orders */}
                            {/* Ready for Review toggle */}
                            {(preorder.status === 'photos_assigned' || preorder.status === 'ready_for_review') && (
                              <Button
                                variant={preorder.status === 'ready_for_review' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  if (preorder.status === 'ready_for_review') {
                                    handleUpdateStatus(preorder.id, 'photos_assigned');
                                  } else {
                                    handleMarkReady(preorder.id);
                                  }
                                }}
                                title={preorder.status === 'ready_for_review' ? 'Undo Ready for Review' : 'Mark Ready for Review'}
                                className={preorder.status === 'ready_for_review' 
                                  ? 'bg-purple-500 hover:bg-purple-600 text-white' 
                                  : 'text-purple-600 hover:bg-purple-50'}
                                data-testid={`toggle-ready-${preorder.id}`}
                              >
                                {preorder.status === 'ready_for_review' ? (
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                ) : null}
                                <Send className="h-3 w-3" />
                              </Button>
                            )}
                            
                            {/* Approved toggle - show for ready_for_review and approved */}
                            {(preorder.status === 'ready_for_review' || preorder.status === 'approved' || preorder.status === 'fulfilled') && (
                              <Button
                                variant={preorder.status === 'approved' || preorder.status === 'fulfilled' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  if (preorder.status === 'approved' || preorder.status === 'fulfilled') {
                                    handleUpdateStatus(preorder.id, 'ready_for_review');
                                  } else {
                                    handleUpdateStatus(preorder.id, 'approved');
                                  }
                                }}
                                title={preorder.status === 'approved' || preorder.status === 'fulfilled' ? 'Undo Approved' : 'Mark Approved'}
                                className={(preorder.status === 'approved' || preorder.status === 'fulfilled')
                                  ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                                  : 'text-blue-600 hover:bg-blue-50'}
                                data-testid={`toggle-approved-${preorder.id}`}
                              >
                                {(preorder.status === 'approved' || preorder.status === 'fulfilled') ? (
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                ) : null}
                                <Eye className="h-3 w-3" />
                              </Button>
                            )}
                            
                            {/* Fulfilled toggle - show for approved and fulfilled */}
                            {(preorder.status === 'approved' || preorder.status === 'fulfilled') && (
                              <Button
                                variant={preorder.status === 'fulfilled' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  if (preorder.status === 'fulfilled') {
                                    handleUpdateStatus(preorder.id, 'approved');
                                  } else {
                                    handleUpdateStatus(preorder.id, 'fulfilled');
                                  }
                                }}
                                title={preorder.status === 'fulfilled' ? 'Undo Fulfilled' : 'Mark Fulfilled'}
                                className={preorder.status === 'fulfilled' 
                                  ? 'bg-green-500 hover:bg-green-600 text-white' 
                                  : 'text-green-600 hover:bg-green-50'}
                                data-testid={`toggle-fulfilled-${preorder.id}`}
                              >
                                {preorder.status === 'fulfilled' ? (
                                  <RotateCcw className="h-3 w-3 mr-1" />
                                ) : null}
                                <CheckCircle className="h-3 w-3" />
                              </Button>
                            )}
                            
                            {/* Resend Free Digital - show for approved/fulfilled orders with free digital */}
                          </>
                        )}
                        {(preorder.status === 'approved' || preorder.status === 'fulfilled') && 
                         preorder.includes_free_digital && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResendDigitalDialog(preorder)}
                            title="Resend Free Digital Image (choose photo & background)"
                            className="text-purple-600 hover:bg-purple-50"
                            data-testid={`resend-digital-${preorder.id}`}
                          >
                            <Mail className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-2 border-stone-300">
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-stone-400 mb-4" />
            <h3 className="text-lg font-medium text-stone-700 mb-2">
              {searchQuery ? 'No Results Found' : 'No Pre-Orders Yet'}
            </h3>
            <p className="text-stone-500">
              {searchQuery 
                ? `No pre-orders match "${searchQuery}". Try a different search.`
                : 'Pre-orders will appear here once clients place them'
              }
            </p>
            {searchQuery && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setSearchQuery('')}
              >
                Clear Search
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Assign Photos Dialog */}
      <Dialog open={assignPhotosDialog.open} onOpenChange={(open) => {
        setAssignPhotosDialog({ open, preorder: null });
        setSelectedGalleryId(null);
        setAvailablePhotos([]);
        setLinkedGalleries([]);
        setPhotoSearchQuery('');
        setAlreadyAssignedPhotos([]);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Photos</DialogTitle>
            <DialogDescription>
              Select photos for {assignPhotosDialog.preorder?.child_name}'s pre-order
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Gallery Selector */}
            {linkedGalleries.length > 0 ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Select Gallery</label>
                <Select value={selectedGalleryId || ''} onValueChange={handleGalleryChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a gallery..." />
                  </SelectTrigger>
                  <SelectContent>
                    {linkedGalleries.length > 1 && (
                      <SelectItem value="all" className="font-medium text-blue-600">
                        📁 All Galleries ({linkedGalleries.reduce((sum, g) => sum + (g.photo_count || 0), 0)} photos total)
                      </SelectItem>
                    )}
                    {linkedGalleries.map(gallery => (
                      <SelectItem key={gallery.id} value={gallery.id}>
                        {gallery.name} ({gallery.photo_count || 0} photos)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedGalleryId === 'all' && linkedGalleries.length > 1 && (
                  <p className="text-xs text-blue-600">
                    Showing photos from all {linkedGalleries.length} linked galleries
                  </p>
                )}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">
                No galleries linked to this event. Link galleries in Pre-Order Events first.
              </p>
            )}
            
            {/* Selected count */}
            {(selectedPhotoIds.length > 0 || alreadyAssignedPhotos.length > 0) && (
              <p className="text-sm text-slate-600">
                Total selected: {selectedPhotoIds.length} photo(s)
                {alreadyAssignedPhotos.length > 0 && (
                  <span className="text-green-600 ml-1">
                    ({alreadyAssignedPhotos.filter(p => selectedPhotoIds.includes(p.id)).length} already assigned)
                  </span>
                )}
              </p>
            )}
            
            {/* Already Assigned Photos - ALWAYS show these at the top */}
            {alreadyAssignedPhotos.length > 0 && (
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <p className="text-sm font-medium text-green-800 mb-2">
                  ✓ Currently Assigned Photos ({alreadyAssignedPhotos.length})
                </p>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {alreadyAssignedPhotos.map(photo => (
                    <div
                      key={photo.id}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 bg-white ${
                        selectedPhotoIds.includes(photo.id) ? 'border-green-500' : 'border-slate-300'
                      }`}
                      onClick={() => togglePhotoSelection(photo.id)}
                    >
                      <img
                        src={getPhotoUrl(photo)}
                        alt={photo.filename || 'Assigned photo'}
                        className="w-full aspect-square object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23eee" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="12">No Preview</text></svg>';
                        }}
                      />
                      {selectedPhotoIds.includes(photo.id) && (
                        <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                      )}
                      <p className="text-[10px] text-slate-700 p-1 truncate bg-white/90" title={photo.filename}>
                        {photo.filename || 'Photo'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Photos grid */}
            {selectedGalleryId && availablePhotos.length > 0 ? (
              <div className="space-y-4">
                {/* Search bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by filename..."
                    value={photoSearchQuery}
                    onChange={(e) => setPhotoSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Newly selected photos from gallery (not in already assigned) */}
                {selectedPhotoIds.filter(id => 
                  availablePhotos.some(p => p.id === id) && 
                  !alreadyAssignedPhotos.some(p => p.id === id)
                ).length > 0 && (
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-2">
                      + Newly Selected ({selectedPhotoIds.filter(id => 
                        availablePhotos.some(p => p.id === id) && 
                        !alreadyAssignedPhotos.some(p => p.id === id)
                      ).length})
                    </p>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                      {availablePhotos
                        .filter(photo => selectedPhotoIds.includes(photo.id) && !alreadyAssignedPhotos.some(p => p.id === photo.id))
                        .map(photo => (
                          <div
                            key={photo.id}
                            className="relative cursor-pointer rounded-lg overflow-hidden border-2 border-amber-500 bg-white"
                            onClick={() => togglePhotoSelection(photo.id)}
                          >
                            <img
                              src={getPhotoUrl(photo)}
                              alt={photo.filename}
                              className="w-full aspect-square object-cover"
                            />
                            <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                              <CheckCircle className="h-6 w-6 text-amber-500" />
                            </div>
                            <p className="text-[10px] text-slate-700 p-1 truncate bg-white/90" title={photo.filename}>
                              {photo.filename}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* All photos */}
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2">
                    All Photos {photoSearchQuery && `(filtered: ${availablePhotos.filter(p => p.filename.toLowerCase().includes(photoSearchQuery.toLowerCase())).length})`}
                  </p>
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-80 overflow-y-auto">
                    {availablePhotos
                      .filter(photo => !photoSearchQuery || photo.filename.toLowerCase().includes(photoSearchQuery.toLowerCase()))
                      .map(photo => (
                      <div
                        key={photo.id}
                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 bg-white ${
                          selectedPhotoIds.includes(photo.id) ? 'border-amber-500' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => togglePhotoSelection(photo.id)}
                      >
                        <img
                          src={getPhotoUrl(photo)}
                          alt={photo.filename}
                          className="w-full aspect-square object-cover"
                        />
                        {selectedPhotoIds.includes(photo.id) && (
                          <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                            <CheckCircle className="h-6 w-6 text-amber-500" />
                          </div>
                        )}
                        <div className="bg-white/90 p-1">
                          <p className="text-[10px] text-slate-700 truncate" title={photo.filename}>
                            {photo.filename}
                          </p>
                          {selectedGalleryId === 'all' && photo.gallery_name && (
                            <p className="text-[9px] text-blue-500 truncate" title={photo.gallery_name}>
                              📁 {photo.gallery_name}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : selectedGalleryId ? (
              <p className="text-center text-slate-500 py-8">
                No photos in this gallery yet.
              </p>
            ) : linkedGalleries.length > 0 ? (
              <p className="text-center text-slate-500 py-8">
                Select a gallery to view photos.
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAssignPhotosDialog({ open: false, preorder: null });
              setSelectedGalleryId(null);
              setAvailablePhotos([]);
              setLinkedGalleries([]);
              setAlreadyAssignedPhotos([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignPhotos}
              className="bg-amber-500 hover:bg-amber-600"
              disabled={selectedPhotoIds.length === 0 || assigningPhotos}
              data-testid="assign-photos-apply-btn"
            >
              {assigningPhotos ? 'Applying...' : `Assign ${selectedPhotoIds.length} Photo(s) Total`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialog.open} onOpenChange={(open) => setViewDetailsDialog({ open, preorder: null, event: null, loading: false })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pre-Order Details</DialogTitle>
            <DialogDescription>
              {viewDetailsDialog.preorder?.child_name} - {viewDetailsDialog.event?.name || 'Unknown Event'}
            </DialogDescription>
          </DialogHeader>
          
          {viewDetailsDialog.loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          ) : viewDetailsDialog.preorder ? (
            <div className="py-4 space-y-6">
              {/* Re-open approved order */}
              {['approved', 'processing', 'fulfilled', 'completed'].includes(viewDetailsDialog.preorder.status) && (
                <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">This order has been {viewDetailsDialog.preorder.status}.</p>
                    <p className="text-xs">Client approved by accident or wants to add something? Re-open it so they can edit via their review link.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-500 text-amber-700 hover:bg-amber-100 shrink-0 ml-3"
                    onClick={() => reopenPreorder(viewDetailsDialog.preorder)}
                    data-testid="reopen-order-btn"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                    Re-open Order
                  </Button>
                </div>
              )}
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Child Name</p>
                  <p className="font-medium">{viewDetailsDialog.preorder.child_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="font-medium">{viewDetailsDialog.preorder.client_name}</p>
                </div>
                {viewDetailsDialog.preorder.team_name && (
                  <div>
                    <p className="text-xs text-slate-500">Team</p>
                    <p className="font-medium">{viewDetailsDialog.preorder.team_name}</p>
                  </div>
                )}
              </div>

              {/* Order Totals Breakdown */}
              <div className="p-4 bg-slate-100 rounded-lg border">
                <h3 className="font-semibold text-slate-800 mb-3">Order Summary</h3>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Subtotal</span>
                    <span>{formatCurrency(viewDetailsDialog.preorder.subtotal || 0)}</span>
                  </div>
                  {(viewDetailsDialog.preorder.discount_amount > 0 || viewDetailsDialog.preorder.discount_code) && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount {viewDetailsDialog.preorder.discount_code && `(${viewDetailsDialog.preorder.discount_code})`}</span>
                      <span>-{formatCurrency(viewDetailsDialog.preorder.discount_amount || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tax</span>
                    <span>{formatCurrency(viewDetailsDialog.preorder.tax || 0)}</span>
                  </div>
                  {viewDetailsDialog.preorder.delivery && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Delivery ({viewDetailsDialog.preorder.delivery.option_name || viewDetailsDialog.preorder.delivery.option || 'Standard'})</span>
                      <span>{formatCurrency(viewDetailsDialog.preorder.delivery.fee || 0)}</span>
                    </div>
                  )}
                  {viewDetailsDialog.preorder.cash_discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Cash Discount (10%)</span>
                      <span>-{formatCurrency(viewDetailsDialog.preorder.cash_discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t mt-2">
                    <span>Total</span>
                    <span className="text-amber-600">{formatCurrency(viewDetailsDialog.preorder.total)}</span>
                  </div>
                  {viewDetailsDialog.preorder.payment_method && (
                    <p className="text-sm text-slate-500 mt-1">
                      Payment: {viewDetailsDialog.preorder.payment_method.toUpperCase()}
                    </p>
                  )}
                </div>
              </div>

              {/* Delivery Information */}
              {viewDetailsDialog.preorder.delivery && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    📦 Delivery Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-amber-700">Delivery Option</p>
                      <p className="font-medium text-amber-900">{viewDetailsDialog.preorder.delivery.option_name || viewDetailsDialog.preorder.delivery.option}</p>
                      <p className="text-sm text-amber-700">Fee: {formatCurrency(viewDetailsDialog.preorder.delivery.fee)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-amber-700">Address Type</p>
                      <p className="font-medium text-amber-900 capitalize">{viewDetailsDialog.preorder.delivery.address_type}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-amber-700">Delivery Address</p>
                      <p className="font-medium text-amber-900">
                        {viewDetailsDialog.preorder.delivery.address}<br />
                        {viewDetailsDialog.preorder.delivery.city}, {viewDetailsDialog.preorder.delivery.postal_code}
                      </p>
                    </div>
                    {viewDetailsDialog.preorder.delivery.notes && (
                      <div className="col-span-2">
                        <p className="text-xs text-amber-700">Delivery Notes</p>
                        <p className="text-sm text-amber-900">{viewDetailsDialog.preorder.delivery.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ordered Items with Templates */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Ordered Items</h3>
                {viewDetailsDialog.preorder.addon_paid_at && viewDetailsDialog.preorder.items?.some(i => i.is_addon) && (
                  <p className="text-xs text-purple-600 mb-2" data-testid="addon-paid-at-note">
                    Add-on items paid on {new Date(viewDetailsDialog.preorder.addon_paid_at).toLocaleString()}
                  </p>
                )}
                <div className="space-y-4">
                  {viewDetailsDialog.preorder.items?.map((item, idx) => (
                    <div key={idx} className={`border rounded-lg p-4 bg-white ${item.is_addon ? 'border-purple-300' : ''}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-slate-800">
                            {item.package_name || item.product_name}
                          </p>
                          <p className="text-sm text-slate-500">Quantity: {item.quantity}</p>
                        </div>
                        <div className="flex gap-1">
                          {item.is_addon && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">
                              <Plus className="h-3 w-3 mr-0.5" />
                              Added later
                            </Badge>
                          )}
                          {item.package_name && (
                            <Badge variant="secondary">Package</Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Package Items */}
                      {item.package_items && item.package_items.length > 0 ? (
                        <div className="space-y-3 mt-3 pt-3 border-t">
                          {item.package_items.map((pkgItem, pkgIdx) => (
                            <div key={`${pkgItem.product_id || 'pkg'}-${pkgIdx}`} className="flex items-start gap-3 p-2 bg-slate-50 rounded">
                              {pkgItem.template && (
                                <div className="flex-shrink-0">
                                  <img 
                                    src={getTemplateUrl(pkgItem.template)}
                                    alt={`${pkgItem.product_name} template`}
                                    className="w-16 h-16 object-contain rounded border bg-white"
                                  />
                                </div>
                              )}
                              <div className="flex-grow">
                                <p className="text-sm font-medium">{pkgItem.product_name}</p>
                                {pkgItem.template && (
                                  <p className="text-xs text-slate-500">Template: {pkgItem.template.name}</p>
                                )}
                                {pkgItem.customizations && Object.keys(pkgItem.customizations).length > 0 && (
                                  <div className="mt-1 text-xs text-slate-600">
                                    {Object.entries(pkgItem.customizations).map(([key, value]) => (
                                      value && typeof value === 'string' && <span key={key} className="mr-2">{key}: {value}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        /* Single Product with Template */
                        <div className="flex items-start gap-3 mt-2">
                          {item.template && (
                            <div className="flex-shrink-0">
                              <img 
                                src={getTemplateUrl(item.template)}
                                alt={`${item.product_name} template`}
                                className="w-20 h-20 object-contain rounded border bg-white"
                              />
                            </div>
                          )}
                          <div>
                            {item.template && (
                              <p className="text-sm text-slate-600">Template: {item.template.name}</p>
                            )}
                            {item.customizations && Object.keys(item.customizations).length > 0 && (
                              <div className="mt-1 text-sm text-slate-600">
                                {Object.entries(item.customizations).map(([key, value]) => (
                                  value && typeof value === 'string' && <div key={key}>{key}: {value}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bonus Items */}
              {(viewDetailsDialog.preorder.includes_free_digital || viewDetailsDialog.preorder.includes_free_wallets) && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Bonus Items</h3>
                  <div className="flex gap-2 flex-wrap">
                    {viewDetailsDialog.preorder.includes_free_digital && (
                      <Badge className="bg-green-100 text-green-700">Free Digital</Badge>
                    )}
                    {viewDetailsDialog.preorder.includes_free_wallets && (
                      <Badge className="bg-blue-100 text-blue-700">2 Free Wallets</Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Assigned Photos */}
              {viewDetailsDialog.preorder.assigned_photos?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Assigned Photos ({viewDetailsDialog.preorder.assigned_photos.length})</h3>
                  <div className="grid grid-cols-6 gap-2">
                    {viewDetailsDialog.preorder.assigned_photos.map(photo => (
                      <img 
                        key={photo.id}
                        src={getPhotoUrl(photo)}
                        alt={photo.filename}
                        className="w-full aspect-square object-cover rounded"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDetailsDialog({ open: false, preorder: null, event: null, loading: false })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Pre-Order Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, preorder: null, loading: false })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Pre-Order</DialogTitle>
            <DialogDescription>
              Update order details, add or remove items
            </DialogDescription>
          </DialogHeader>
          
          {editDialog.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
            </div>
          ) : editDialog.preorder ? (
            <div className="space-y-6">
              {/* Customer Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Child's Name</Label>
                  <Input
                    value={editForm.child_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, child_name: e.target.value }))}
                    placeholder="Child's name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Team Name</Label>
                  <Input
                    value={editForm.team_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, team_name: e.target.value }))}
                    placeholder="Team name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Division</Label>
                  <Input
                    value={editForm.division}
                    onChange={(e) => setEditForm(prev => ({ ...prev, division: e.target.value }))}
                    placeholder="Division"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Jersey #</Label>
                  <Input
                    value={editForm.jersey_number}
                    onChange={(e) => setEditForm(prev => ({ ...prev, jersey_number: e.target.value }))}
                    placeholder="Jersey number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Parent/Client Name</Label>
                  <Input
                    value={editForm.client_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="Client name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.client_email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="Email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editForm.client_phone}
                    onChange={(e) => setEditForm(prev => ({ ...prev, client_phone: e.target.value }))}
                    placeholder="Phone"
                  />
                </div>
              </div>

              {/* Current Items */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Order Items</h3>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="w-24">Price</TableHead>
                        <TableHead className="w-24">Qty</TableHead>
                        <TableHead className="w-24">Total</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editForm.items.map((item, index) => (
                        <TableRow key={`${index}-${item.product_id || item.package_id || 'item'}`}>
                          <TableCell>
                            {item.product_name || item.package_name || item.name || 'Unknown Item'}
                            {(() => {
                              const prod = item.product_id ? allProducts.find(p => p.id === item.product_id) : null;
                              if (!prod?.variations?.length) return null;
                              return prod.variations.map(v => (
                                <div key={v.name} className="mt-1 flex items-center gap-1">
                                  <span className="text-xs text-slate-500">{v.name}:</span>
                                  <select
                                    className="text-xs border rounded px-1 py-0.5 bg-white"
                                    value={item.selected_variations?.[v.name] || ''}
                                    onChange={(e) => setEditItemVariation(index, prod, v.name, e.target.value || null)}
                                    data-testid={`edit-item-variation-${index}`}
                                  >
                                    <option value="">None</option>
                                    {(v.options || []).map(o => (
                                      <option key={o.name} value={o.name}>
                                        {o.name}{(o.price_adjustment || 0) > 0 ? ` (+$${o.price_adjustment})` : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ));
                            })()}
                          </TableCell>
                          <TableCell>{formatCurrency(item.price || 0)}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(index, parseInt(e.target.value) || 1)}
                              className="w-16 h-8"
                            />
                          </TableCell>
                          <TableCell>{formatCurrency((item.price || 0) * (item.quantity || 1))}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItemFromEdit(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {editForm.items.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-slate-500 py-4">
                            No items in order
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Subtotal */}
                <div className="flex justify-end mt-2">
                  <div className="text-right">
                    <span className="text-slate-500 mr-2">New Subtotal:</span>
                    <span className="font-bold text-lg">{formatCurrency(getEditSubtotal())}</span>
                  </div>
                </div>
              </div>

              {/* Request Payment from Client */}
              <div className="border rounded-lg p-3 bg-amber-50/50 border-amber-200">
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-1 text-sm">
                  <DollarSign className="h-4 w-4 text-amber-600" />
                  Request Payment from Client (optional)
                </h3>
                {editDialog.preorder?.admin_payment_request?.status === 'pending' ? (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-slate-600">
                      Pending request: <span className="font-semibold">{formatCurrency(editDialog.preorder.admin_payment_request.amount)}</span>
                      {' — '}{editDialog.preorder.admin_payment_request.description}
                    </p>
                    <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => cancelPaymentRequest(editDialog.preorder.id)} data-testid="cancel-payment-request-btn">
                      Cancel Request
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 mb-2">
                      Emails the client a Stripe payment link. Or change the order freely and collect the difference yourself.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="Amount"
                        value={payReq.amount}
                        onChange={(e) => setPayReq(prev => ({ ...prev, amount: e.target.value }))}
                        className="sm:w-28 h-9"
                        data-testid="payment-request-amount"
                      />
                      <Input
                        placeholder="Description (e.g. Swapped trader cards for magnetic wallets)"
                        value={payReq.description}
                        onChange={(e) => setPayReq(prev => ({ ...prev, description: e.target.value }))}
                        className="flex-1 h-9"
                        data-testid="payment-request-description"
                      />
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-9" onClick={sendPaymentRequest} disabled={payReq.sending} data-testid="send-payment-request-btn">
                        {payReq.sending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Send Request'}
                      </Button>
                    </div>
                    {getEditSubtotal() > (editDialog.preorder?.subtotal || 0) && (
                      <button
                        type="button"
                        className="text-xs text-amber-700 underline mt-1"
                        onClick={() => setPayReq(prev => ({ ...prev, amount: (getEditSubtotal() - (editDialog.preorder?.subtotal || 0)).toFixed(2) }))}
                        data-testid="use-difference-btn"
                      >
                        Use item difference: {formatCurrency(getEditSubtotal() - (editDialog.preorder?.subtotal || 0))}
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Add Products */}
              {eventProducts.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Add Products</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                    {eventProducts.map(product => (
                      <Button
                        key={product.id}
                        variant="outline"
                        size="sm"
                        onClick={() => addItemToEdit(product)}
                        className="justify-start text-left h-auto py-2"
                      >
                        <Plus className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{product.name}</span>
                        <span className="ml-auto text-amber-600">{formatCurrency(product.price)}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div className="space-y-2">
                <Label>Admin Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Internal notes about this order..."
                  rows={2}
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, preorder: null, loading: false })}>
              Cancel
            </Button>
            <Button 
              onClick={savePreorderEdit}
              disabled={editDialog.loading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {editDialog.loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(open) => { 
        if (!open) {
          setPaymentDialog({ open: false, preorder: null }); 
          setPaymentAmount('');
          setPaymentNotes('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record a payment or adjust the order total.
            </DialogDescription>
          </DialogHeader>
          {paymentDialog.preorder && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{paymentDialog.preorder.child_name}</p>
                <p className="text-sm text-slate-500">{paymentDialog.preorder.client_email || paymentDialog.preorder.parent_email}</p>
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Order Total:</span>
                    <span>{formatCurrency(paymentDialog.preorder.total)}</span>
                  </div>
                  {paymentDialog.preorder.delivery_fee > 0 && (
                    <div className="flex justify-between text-sm text-slate-400">
                      <span>(includes ${paymentDialog.preorder.delivery_fee?.toFixed(2)} delivery)</span>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-red-500 hover:text-red-700"
                        onClick={async () => {
                          const newTotal = (paymentDialog.preorder.total || 0) - (paymentDialog.preorder.delivery_fee || 0);
                          if (window.confirm(`Remove $${paymentDialog.preorder.delivery_fee?.toFixed(2)} delivery fee? New total will be $${newTotal.toFixed(2)}`)) {
                            try {
                              await axios.put(`${API}/preorders/${paymentDialog.preorder.id}/adjust-total`, {
                                new_total: newTotal,
                                reason: 'Delivery fee removed'
                              });
                              toast.success('Delivery fee removed');
                              setPaymentDialog({ open: false, preorder: null });
                              fetchPreorders();
                            } catch (error) {
                              toast.error('Failed to adjust total');
                            }
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  {(paymentDialog.preorder.amount_paid > 0) && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Amount Paid:</span>
                      <span>{formatCurrency(paymentDialog.preorder.amount_paid)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Balance Owing:</span>
                    <span className="text-amber-600">
                      {formatCurrency((paymentDialog.preorder.total || 0) - (paymentDialog.preorder.amount_paid || 0))}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Quick actions for $0 balance or small adjustments */}
              {(paymentDialog.preorder.total || 0) - (paymentDialog.preorder.amount_paid || 0) <= 0.01 && paymentDialog.preorder.payment_status !== 'paid' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700 mb-2">Balance is $0 or less. Mark as paid?</p>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/preorders/${paymentDialog.preorder.id}/mark-paid`);
                        toast.success('Order marked as paid');
                        setPaymentDialog({ open: false, preorder: null });
                        fetchPreorders();
                      } catch (error) {
                        toast.error('Failed to mark as paid');
                      }
                    }}
                  >
                    Mark as Paid
                  </Button>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Payment Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder={((paymentDialog.preorder.total || 0) - (paymentDialog.preorder.amount_paid || 0)).toFixed(2)}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="pl-7"
                    data-testid="preorder-payment-amount"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Leave empty to pay full balance. You can enter more than the balance if the discount was removed.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="etransfer">e-Transfer</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="e.g., Reference number, date received..."
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPaymentDialog({ open: false, preorder: null }); setPaymentAmount(''); setPaymentNotes(''); }}>
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleMarkPaid}
            >
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Digital Downloads Approval Queue Dialog */}
      <Dialog open={showDigitalsDialog} onOpenChange={(open) => { if (!sendingDigitals) setShowDigitalsDialog(open); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="digitals-dialog">
          <DialogHeader>
            <DialogTitle>Digital Downloads — Awaiting Your Approval</DialogTitle>
            <DialogDescription>
              Paid + approved orders with purchased digital images. Preview each order's images, then release the download emails you approve. Nothing is sent automatically.
            </DialogDescription>
          </DialogHeader>

          {loadingDigitals ? (
            <div className="py-10 text-center text-stone-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading pending orders...
            </div>
          ) : pendingDigitals.length === 0 ? (
            <div className="py-10 text-center">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
              <p className="text-stone-600 font-medium">All caught up!</p>
              <p className="text-sm text-stone-400">No digital downloads are waiting for approval.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-stone-500">
                  {pendingDigitals.filter(p => p.complete).length} ready to send
                  {pendingDigitals.some(p => !p.complete) && ` · ${pendingDigitals.filter(p => !p.complete).length} need selections`}
                </p>
                <Button variant="outline" size="sm" onClick={selectAllDigitals} data-testid="select-all-digitals-btn">
                  {selectedDigitals.length === pendingDigitals.filter(p => p.complete).length && selectedDigitals.length > 0 ? 'Deselect All' : 'Select All Ready'}
                </Button>
              </div>
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {pendingDigitals.map((p) => (
                  <div
                    key={p.preorder_id}
                    className={`flex items-center gap-3 p-3 rounded-sm border ${
                      !p.complete ? 'bg-stone-50 border-stone-200 opacity-70' :
                      selectedDigitals.includes(p.preorder_id) ? 'bg-blue-50 border-blue-300' : 'bg-white border-stone-200'
                    }`}
                    data-testid={`digital-row-${p.preorder_id}`}
                  >
                    <Checkbox
                      checked={selectedDigitals.includes(p.preorder_id)}
                      disabled={!p.complete}
                      onCheckedChange={() => p.complete && toggleDigital(p.preorder_id)}
                      data-testid={`digital-checkbox-${p.preorder_id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-800 truncate">
                        {p.child_name} {p.team_name ? <span className="text-stone-400 font-normal">({p.team_name})</span> : null}
                      </p>
                      <p className="text-sm text-stone-500 truncate">
                        {p.parent_name || 'Parent'} • {p.email || 'No email'} • {p.labels.join(', ')}
                      </p>
                    </div>
                    {p.complete ? (
                      <Badge variant="outline" className="text-xs shrink-0">{p.file_count} image{p.file_count === 1 ? '' : 's'}</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 text-xs shrink-0">{p.missing_reason}</Badge>
                    )}
                    {p.preview_path && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(p.preview_path, '_blank')}
                        data-testid={`preview-digital-btn-${p.preorder_id}`}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        Preview
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {sendingDigitals && (
            <div className="space-y-2">
              <div className="w-full bg-stone-100 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${digitalProgress.total ? (digitalProgress.current / digitalProgress.total) * 100 : 0}%` }} />
              </div>
              <p className="text-sm text-stone-500 text-center">
                Sending {digitalProgress.current} / {digitalProgress.total}... emails are sent from the server, so it's safe to close this page
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDigitalsDialog(false)} disabled={sendingDigitals}>
              Close
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={releaseDigitals}
              disabled={sendingDigitals || selectedDigitals.length === 0}
              data-testid="release-digitals-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {sendingDigitals ? 'Sending...' : `Approve & Send ${selectedDigitals.length || ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Problem Report Dialog */}
      {/* Resend Free Digital Dialog */}
      <Dialog open={resendDigital.open} onOpenChange={(open) => !open && setResendDigital(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="resend-digital-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              Resend Free Digital Image
            </DialogTitle>
            <DialogDescription>
              Confirm the photo and background, then resend. The image is regenerated with the background composited in.
            </DialogDescription>
          </DialogHeader>
          {resendDigital.preorder && (
            <div className="space-y-4">
              {!resendDigital.preorder.free_digital_background_id && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800" data-testid="no-background-warning">
                  This order has <strong>no background selected</strong> — that's why the client received the image without one. Pick a background below before resending.
                </div>
              )}
              <div>
                <Label className="text-sm font-medium">Photo</Label>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-2 max-h-48 overflow-y-auto">
                  {(resendDigital.preorder.assigned_photos || []).map(photo => (
                    <div
                      key={photo.id}
                      onClick={() => setResendDigital(prev => ({ ...prev, photoId: photo.id }))}
                      className={`cursor-pointer rounded-lg border-2 overflow-hidden ${resendDigital.photoId === photo.id ? 'border-purple-500 ring-2 ring-purple-300' : 'border-transparent hover:border-slate-300'}`}
                      data-testid={`resend-photo-${photo.id}`}
                    >
                      <img src={photo.url?.startsWith('http') ? photo.url : `${process.env.REACT_APP_BACKEND_URL}${photo.url}`} alt={photo.filename} className="w-full h-20 object-cover" />
                    </div>
                  ))}
                  {(resendDigital.preorder.assigned_photos || []).length === 0 && (
                    <p className="text-sm text-slate-500 col-span-full">No photos assigned to this order.</p>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Background</Label>
                <select
                  className="w-full border rounded-md h-9 px-2 text-sm mt-1 bg-white"
                  value={resendDigital.backgroundId}
                  onChange={(e) => setResendDigital(prev => ({ ...prev, backgroundId: e.target.value }))}
                  data-testid="resend-background-select"
                >
                  <option value="">No background (photo as-is)</option>
                  {backgroundsList.map(bg => (
                    <option key={bg.id} value={bg.id}>{bg.name}</option>
                  ))}
                </select>
                {resendDigital.backgroundId && (() => {
                  const bg = backgroundsList.find(b => b.id === resendDigital.backgroundId);
                  return bg?.url ? (
                    <img src={bg.url.startsWith('http') ? bg.url : `${process.env.REACT_APP_BACKEND_URL}${bg.url}`} alt={bg.name} className="mt-2 h-24 rounded-lg border object-cover" />
                  ) : null;
                })()}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendDigital(prev => ({ ...prev, open: false }))}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              disabled={resendDigital.sending || !resendDigital.photoId}
              onClick={sendResendDigital}
              data-testid="resend-digital-send-btn"
            >
              {resendDigital.sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Resend Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Requests Modal */}
      <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="upgrade-requests-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Upgrade Requests
            </DialogTitle>
            <DialogDescription>
              Client requests to swap items or add options (equal or greater value only). Approving a paid upgrade emails the client to pay the difference; $0 upgrades apply instantly and re-open the order.
            </DialogDescription>
          </DialogHeader>
          {upgradeReqs.length === 0 ? (
            <p className="text-sm text-slate-500 py-4" data-testid="no-upgrade-requests">No upgrade requests right now.</p>
          ) : (
            <div className="space-y-3">
              {upgradeReqs.map((r) => {
                const ur = r.upgrade_request || {};
                const oldName = ur.old_item?.package_name || ur.old_item?.product_name;
                const newName = ur.new_item?.package_name || ur.new_item?.product_name;
                return (
                  <div key={r.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`upgrade-request-row-${r.id}`}>
                    <div>
                      <p className="font-medium text-sm">
                        {r.child_name} <span className="text-slate-400 font-normal">({r.client_name})</span>
                      </p>
                      <p className="text-xs text-slate-600">{oldName} → {newName}</p>
                      <p className="text-xs text-slate-500">
                        Difference: <span className="font-semibold">{formatCurrency(ur.total_difference || 0)}</span>
                        {ur.requested_at && ` · ${new Date(ur.requested_at).toLocaleString()}`}
                      </p>
                    </div>
                    <div className="flex gap-2 items-center">
                      {ur.status === 'pending_admin' ? (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 h-8"
                            disabled={upgradeActionLoading === r.id + 'approve'}
                            onClick={() => handleUpgradeAction(r.id, 'approve')}
                            data-testid={`approve-upgrade-${r.id}`}
                          >
                            {upgradeActionLoading === r.id + 'approve' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-red-300 text-red-600 hover:bg-red-50"
                            disabled={upgradeActionLoading === r.id + 'reject'}
                            onClick={() => handleUpgradeAction(r.id, 'reject')}
                            data-testid={`reject-upgrade-${r.id}`}
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                          {ur.status === 'approved_awaiting_payment' ? 'Approved — awaiting payment' : 'Awaiting client payment'}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={problemReport.open} onOpenChange={(open) => setProblemReport(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="problem-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Problem Orders Report
            </DialogTitle>
          </DialogHeader>
          {problemReport.loading ? (
            <div className="py-10 text-center text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              Scanning and auto-healing all orders...
            </div>
          ) : problemReport.data && (
            <div className="space-y-6">
              <p className="text-sm text-slate-500" data-testid="problem-report-healed-count">
                {problemReport.data.healed_count} order(s) were auto-repaired during this scan (legacy items linked, bonuses corrected).
                {problemReport.data.duplicates_removed > 0 && ` ${problemReport.data.duplicates_removed} duplicate order record(s) were removed.`}
              </p>

              {(problemReport.data.duplicates_archive || []).length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">
                    Removed duplicates — kept for your review ({problemReport.data.duplicates_archive.length})
                  </h3>
                  <p className="text-xs text-slate-500 mb-2">
                    These were extra copies of orders that still exist in your list (same order ID). The original order was kept — nothing was lost. They're archived here so you can double-check.
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {problemReport.data.duplicates_archive.map((d, i) => (
                      <div key={`${d.id}-${d.archived_at || i}`} className="border border-stone-200 rounded-lg p-2.5 bg-stone-50 text-xs" data-testid={`dup-archive-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-slate-700 truncate">
                            {d.child_name || 'Recovered order'} <span className="text-slate-400 font-normal">({d.parent_name || d.client_name || 'Parent'} — {d.parent_email || d.client_email || 'no email'})</span>
                          </p>
                          <span className="text-slate-400 shrink-0">${(d.total || 0).toFixed(2)}</span>
                        </div>
                        <p className="text-slate-500 mt-0.5 truncate">
                          {(d.items || []).map(it => `${it.product_name || it.package_name} x${it.quantity || 1}`).join(', ') || 'No items'}
                        </p>
                        <p className="text-slate-400 mt-0.5">
                          Ordered {d.created_at ? new Date(d.created_at).toLocaleDateString() : '?'} • Removed {d.archived_at ? new Date(d.archived_at).toLocaleDateString() : '?'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-semibold text-slate-800 mb-2">
                  Items that can't be matched to any product ({problemReport.data.unresolved_orders.length})
                </h3>
                {problemReport.data.unresolved_orders.length === 0 ? (
                  <p className="text-sm text-green-600">None — every order item is linked to a product. 🎉</p>
                ) : (
                  <div className="space-y-2">
                    {problemReport.data.unresolved_orders.map(o => (
                      <div key={o.id} className="border border-red-200 rounded-lg p-3 bg-red-50/50" data-testid={`unresolved-order-${o.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-800">{o.child_name} <span className="text-slate-500 font-normal">({o.parent_name} — {o.parent_email})</span></p>
                            <p className="text-xs text-red-600 mt-1">{o.problem_items.join('; ')}</p>
                          </div>
                          <Badge variant="outline" className="text-xs">{o.status}</Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Fix: edit the order and re-add these items from your current product list.</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="font-semibold text-slate-800">
                    Approved orders with incomplete selections ({problemReport.data.approved_incomplete.length})
                  </h3>
                  {problemReport.data.approved_incomplete.length > 0 && (
                    <Button
                      size="sm"
                      className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={bulkReopenWithApology}
                      disabled={bulkApologyLoading}
                      data-testid="bulk-reopen-apology-btn"
                    >
                      {bulkApologyLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                      Re-open All & Send Apology ({problemReport.data.approved_incomplete.length})
                    </Button>
                  )}
                </div>
                {problemReport.data.approved_incomplete.length === 0 ? (
                  <p className="text-sm text-green-600">None — all approved orders have complete selections. 🎉</p>
                ) : (
                  <div className="space-y-2">
                    {problemReport.data.approved_incomplete.map(o => (
                      <div key={o.id} className="border border-amber-200 rounded-lg p-3 bg-amber-50/50" data-testid={`incomplete-order-${o.id}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800">{o.child_name} <span className="text-slate-500 font-normal">({o.parent_name} — {o.parent_email})</span></p>
                            <p className="text-xs text-amber-700 mt-1">{o.missing.join('; ')}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 border-blue-400 text-blue-600 hover:bg-blue-50"
                            onClick={() => reopenPreorder({ id: o.id, child_name: o.child_name })}
                            data-testid={`report-reopen-${o.id}`}
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Re-open
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Final Files Dialog */}
      <Dialog open={finalsDialog.open} onOpenChange={(open) => setFinalsDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-lg" data-testid="final-files-dialog">
          <DialogHeader>
            <DialogTitle>Final Files — {finalsDialog.preorder?.child_name}</DialogTitle>
            <DialogDescription>
              Upload your finished designs (memory mates, cards, etc.). They appear on this client's digital download page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              multiple
              onChange={(e) => { uploadFinalFiles(e.target.files); e.target.value = ''; }}
              disabled={finalsDialog.uploading}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white hover:file:bg-purple-700 file:cursor-pointer"
              data-testid="final-files-input"
            />
            {finalsDialog.uploading && (
              <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</p>
            )}
            {finalsDialog.files.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {finalsDialog.files.map(f => (
                  <div key={f.id} className="flex items-center justify-between gap-2 border rounded-lg p-2.5 text-sm" data-testid={`final-file-${f.id}`}>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-700">{f.filename}</p>
                      <p className="text-xs text-slate-400">Uploaded {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ''}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-600 shrink-0" onClick={() => deleteFinalFile(f.id)} data-testid={`delete-final-${f.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : !finalsDialog.uploading && (
              <p className="text-sm text-slate-400">No final files uploaded yet</p>
            )}
            <p className="text-xs text-slate-400">
              Tip: once files are uploaded (and the order is approved + paid), send the download email from the Digital Downloads queue.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreOrdersPage;
